/**
 * Documents to Sign Page — Consumer Portal (Borrower Sign Queue redesign)
 *
 * Lists pending + recently-signed e-signature requests for the authenticated
 * consumer, across all lenders. One click opens DocuSeal via the
 * DocumentSigningModal.
 *
 * Spec: /tmp/design-pass/borrower/specs/sign-and-documents.md (Screen 1)
 *
 * Surfaces:
 * - UrgentSignBanner — red strip when ≥1 urgent pending row.
 * - Pending / Signed tabs with badge counts.
 * - SignQueueRow per row (pending or signed variant).
 * - AstorWalkBlock — mint-bordered walkthrough offer beneath the tab list.
 *
 * Rules:
 * - Glass variants only (rule 11b)
 * - sx + theme tokens only (rule 11c)
 * - All copy via useI18n() / t() (rule 13)
 * - White-label safe — lender business name comes from the envelope (rule 11d)
 * - DocumentSigningModal is SplitPanel-based (rule 14)
 * - No window.matchMedia in initial render (rule 11e)
 * - 44px touch targets everywhere (portals.md)
 */

import GavelIcon from '@mui/icons-material/Gavel';
import {
  EmptyState,
  GlassAlert,
  GlassButton,
  GlassChip,
  GlassTabs,
  iconSizes,
  ShimmerLoader,
  Stack,
  Typography,
  useI18n,
} from '@ops-matrix/design-system-ui';
import { useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { AstorWalkBlock } from '../../components/documents/AstorWalkBlock';
import { SignQueueRow } from '../../components/documents/SignQueueRow';
import { UrgentSignBanner } from '../../components/documents/UrgentSignBanner';
import { DocumentSigningModal } from '../../components/esign/DocumentSigningModal';
import { PageTemplate } from '../../components/layout/PageTemplate';
import { env } from '../../config/env';
import { trpc } from '../../lib/trpc/client';
import { useListPendingSignatures } from '../../lib/trpc/hooks';

const HOURS_72_MS = 72 * 60 * 60 * 1000;

function formatDate(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function shortMonoId(envelopeId: string): string {
  if (!envelopeId) return '';
  // First two dash-separated segments — keeps the row compact and matches
  // the v3 "monoId" convention used elsewhere in the consumer portal.
  return envelopeId.split('-').slice(0, 2).join('-');
}

type PendingRow = {
  envelopeId: string;
  recipientId: string;
  envelopeName: string;
  senderBusinessName: string;
  senderName: string;
  role: string;
  signingOrder: number;
  status: string;
  docuSealSubmitterId: number;
  sentAt: string;
  expiresAt: string;
  waitingOnSignerName: string;
};

type SignedRow = {
  envelopeId: string;
  recipientId: string;
  envelopeName: string;
  senderBusinessName: string;
  signedAt: string;
  retainedCopyId: string;
};

function deriveUrgency(row: PendingRow): 'high' | 'normal' {
  if (!row.expiresAt) return 'normal';
  const expiresAt = new Date(row.expiresAt);
  if (Number.isNaN(expiresAt.getTime())) return 'normal';
  const delta = expiresAt.getTime() - Date.now();
  return delta > 0 && delta <= HOURS_72_MS ? 'high' : 'normal';
}

export default function DocumentsToSignPage() {
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState<'pending' | 'signed'>('pending');
  const [activeRow, setActiveRow] = useState<PendingRow | null>(null);

  const flagEnabled = env.VITE_ENABLE_TERMSHEET_SIGNING_V2;
  const query = useListPendingSignatures({ enabled: flagEnabled });
  const utils = trpc.useUtils();

  const pending = useMemo<PendingRow[]>(() => (query.data?.pending ?? []) as PendingRow[], [query.data?.pending]);
  const signed = useMemo<SignedRow[]>(() => (query.data?.signed ?? []) as SignedRow[], [query.data?.signed]);

  if (!flagEnabled) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleSign = (row: PendingRow) => setActiveRow(row);
  const handleModalClose = () => setActiveRow(null);
  const handleSigningComplete = () => {
    void query.refetch();
    setActiveRow(null);
  };

  // Signed-row download — fetch a fresh presigned URL via the existing
  // CRM path and open in a new tab. Mirrors the original SignedSignatureCard
  // handler so we keep the working flow while shipping the new row layout.
  const handleDownloadSigned = async (retainedCopyId: string) => {
    if (!retainedCopyId) return;
    try {
      const result = await utils.esign.downloadRetainedCopy.fetch({ id: retainedCopyId });
      if (result?.url) {
        window.open(result.url, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      console.error('Download signed document failed:', err);
    }
  };

  const urgentRows = pending.filter((row) => deriveUrgency(row) === 'high');
  // The "sign all" CTA focuses the first urgent row's signing flow — keeps
  // behavior predictable while the real bulk-sign endpoint is in flight.
  // TODO(#sign-queue-bulk): when GET /bulk-sign lands, swap to it.
  const handleSignAll = () => {
    const firstUrgent = urgentRows[0];
    if (firstUrgent) setActiveRow(firstUrgent);
  };
  // Pull the earliest urgent deadline for the strip's deadline copy.
  const earliestUrgentDeadline = urgentRows
    .map((row) => new Date(row.expiresAt).getTime())
    .filter((t) => !Number.isNaN(t))
    .sort((a, b) => a - b)[0];
  const urgentDeadlineLabel = earliestUrgentDeadline
    ? new Date(earliestUrgentDeadline).toLocaleString(undefined, {
        weekday: 'short',
        hour: 'numeric',
        minute: '2-digit',
      })
    : undefined;

  const pendingCount = pending.length;
  const signedCount = signed.length;
  const showUrgentStrip = activeTab === 'pending' && urgentRows.length > 0;
  const showAstorWalk = activeTab === 'pending' && pending.length > 0;

  return (
    <PageTemplate
      titleKey="consumer.signQueue.pageTitle"
      descriptionKey="consumer.signQueue.pageSubtitle"
      icon={GavelIcon}
      variant="list"
    >
      <Stack spacing={3} sx={{ pt: 2 }}>
        {showUrgentStrip && (
          <UrgentSignBanner
            urgentCount={urgentRows.length}
            deadline={urgentDeadlineLabel}
            loanContext={(() => {
              const first = urgentRows[0];
              if (!first) return undefined;
              // The PendingRow shape doesn't carry the loan address yet — fall back to a
              // sender-based loan label + doc count. When the proto adds an address we
              // can show "Marlin Ct loan · {N} documents".
              const senderLabel = first.senderBusinessName || first.envelopeName;
              return senderLabel
                ? t('consumer.signQueue.urgentStripLoanContext', {
                    defaultValue: '{{loan}} loan · {{count}} documents',
                    loan: senderLabel,
                    count: urgentRows.length,
                  })
                : t('consumer.signQueue.urgentStripDocCount', {
                    defaultValue: '{{count}} documents to sign',
                    count: urgentRows.length,
                  });
            })()}
            onSignAll={handleSignAll}
          />
        )}

        <GlassTabs
          tabs={[
            {
              value: 'pending',
              label: (
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <span>{t('consumer.signQueue.tabPending')}</span>
                  {pendingCount > 0 && (
                    <GlassChip label={String(pendingCount)} size="small" color="error" sx={{ minHeight: 20 }} />
                  )}
                </Stack>
              ),
            },
            {
              value: 'signed',
              label: (
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <span>{t('consumer.signQueue.tabSigned')}</span>
                  {signedCount > 0 && (
                    <GlassChip label={String(signedCount)} size="small" color="success" sx={{ minHeight: 20 }} />
                  )}
                </Stack>
              ),
            },
          ]}
          value={activeTab}
          onChange={(_, v) => setActiveTab(v as 'pending' | 'signed')}
          aria-label={t('consumer.signQueue.pageTitle')}
        />

        {query.isLoading && (
          <Stack spacing={2} role="status" aria-live="polite">
            {[0, 1, 2].map((i) => (
              <ShimmerLoader key={i} variant="rectangular" width="100%" height={96} />
            ))}
          </Stack>
        )}

        {!query.isLoading && query.isError && (
          <GlassAlert severity="error" role="alert">
            <Stack direction="row" spacing={2} sx={{ alignItems: 'center', width: '100%' }}>
              <Typography variant="body2" sx={{ flex: 1 }}>
                {t('consumer.signQueue.errorTitle')}
              </Typography>
              <GlassButton variant="outlined" size="small" onClick={() => void query.refetch()}>
                {t('consumer.signQueue.retry')}
              </GlassButton>
            </Stack>
          </GlassAlert>
        )}

        {!query.isLoading && !query.isError && activeTab === 'pending' && (
          <Stack spacing={2} id="sign-queue-panel-pending" role="region" aria-labelledby="sign-queue-tab-pending">
            {pending.length === 0 ? (
              <EmptyState
                title={t('consumer.signQueue.emptyPendingTitle')}
                description={t('consumer.signQueue.emptyPendingSubtitle')}
                icon={<GavelIcon sx={{ fontSize: iconSizes['3xl'], color: 'text.disabled' }} aria-hidden="true" />}
              />
            ) : (
              pending.map((row) => (
                <SignQueueRow
                  key={row.recipientId}
                  variant="pending"
                  id={row.recipientId}
                  title={row.envelopeName || t('consumer.signQueue.pageTitle')}
                  loan={shortMonoId(row.envelopeId)}
                  sentBy={row.senderName || row.senderBusinessName}
                  sentAt={formatDate(row.sentAt)}
                  pages={1}
                  urgency={deriveUrgency(row)}
                  signDisabled={!row.docuSealSubmitterId || !!row.waitingOnSignerName}
                  signDisabledReason={
                    row.waitingOnSignerName
                      ? t('consumer.documents.waiting', { name: row.waitingOnSignerName })
                      : undefined
                  }
                  onReview={() => handleSign(row)}
                  onSign={() => handleSign(row)}
                />
              ))
            )}
          </Stack>
        )}

        {!query.isLoading && !query.isError && activeTab === 'signed' && (
          <Stack spacing={2} id="sign-queue-panel-signed" role="region" aria-labelledby="sign-queue-tab-signed">
            {signed.length === 0 ? (
              <EmptyState
                title={t('consumer.signQueue.emptySignedTitle')}
                description={t('consumer.documents.signedEmptyHelp')}
                icon={<GavelIcon sx={{ fontSize: iconSizes['3xl'], color: 'text.disabled' }} aria-hidden="true" />}
              />
            ) : (
              signed.map((row) => (
                <SignQueueRow
                  key={row.recipientId}
                  variant="signed"
                  id={row.recipientId}
                  title={row.envelopeName || t('consumer.signQueue.pageTitle')}
                  loan={shortMonoId(row.envelopeId)}
                  signedAt={formatDate(row.signedAt)}
                  pages={1}
                  downloadDisabled={!row.retainedCopyId}
                  onDownload={() => {
                    if (!row.retainedCopyId) return;
                    void handleDownloadSigned(row.retainedCopyId);
                  }}
                />
              ))
            )}
          </Stack>
        )}

        {showAstorWalk && (
          <AstorWalkBlock
            onWalkMeThrough={() => {
              // TODO(#astor-walkthrough): launch the doc walkthrough surface
              //   (likely an AstorChatFlyover scoped to the first urgent doc).
              //   For now, focus the first urgent (or first pending) row's
              //   signing flow so the user still moves forward.
              const target = urgentRows[0] ?? pending[0];
              if (target) setActiveRow(target);
            }}
          />
        )}
      </Stack>

      <DocumentSigningModal
        open={activeRow !== null}
        onClose={handleModalClose}
        submitterId={activeRow?.docuSealSubmitterId ?? null}
        documentName={activeRow?.envelopeName}
        onSigningComplete={handleSigningComplete}
      />
    </PageTemplate>
  );
}
